<template>
    <div class="card" style="min-height: 200px">
        <div class="table-responsive">
            <table class="table">
                <thead>
                <tr>
                    <th v-if="selectable" style="width: 5%">
                        <input
                            type="checkbox"
                            :checked="allSelected"
                            @change="toggleSelectAll($event.target.checked)"
                        >
                    </th>
                    <th>Date</th>
                    <th>Bill Number</th>
                    <th class="text-right">Bill Amount</th>
                    <th>Party Name</th>
                    <th>Action</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, i) in purchase" :key="i">
                    <td v-if="selectable">
                        <input type="checkbox" v-model="item.selected">
                    </td>
                    <td>{{ item.date }}</td>
                    <td>{{ item.bill_number }}</td>
                    <td class="text-right">{{ item.bill_amount }}</td>
                    <td>{{ item.party_name }}</td>
                    <td>
                        <div class="d-flex">
                            <button
                                class="btn btn-primary btn-sm me-1"
                                @click="onView(item)"
                                style="cursor: pointer"
                            >
                                <i class="fas fa-eye"></i>
                            </button>
                            <div class="dropdown">
                                <button class="btn btn-secondary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown">
                                    <i class="fas fa-print"></i>
                                </button>
                                <ul class="dropdown-menu">
                                    <li>
                                        <a class="dropdown-item" href="#" @click.prevent="goToPrint(item.id, false)">
                                            Print
                                        </a>
                                    </li>
                                    <li>
                                        <a class="dropdown-item" href="#" @click.prevent="goToPrint(item.id, true)">
                                            Print with Signature
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </td>
                </tr>
                </tbody>
            </table>
            <p v-if="!purchase.length" class="text-center mt-5">No data</p>
        </div>
    </div>
</template>

<script setup>
import { computed, inject } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import handlePurchase from '@/services/modules/purchase'
import handleBarcode from '@/services/modules/barcode'
import pdfPrinter from '@/services/utils/pbPdfPrinter'
import useDate from '@/services/utils/day'

const props = defineProps({
    purchase: {
        type: Array,
        default: () => []
    },
    selectable: {
        type: Boolean,
        default: true
    },
    companyInfo: {
        type: Object,
        default: null
    },
    customizationPdfFields: {
        type: Object,
        default: () => ({})
    }
})

const emit = defineEmits(['onView'])

const route = useRoute()
const store = useStore()
const showError = inject('showError')
const { fetchPurchaseBill } = handlePurchase()
const { fetchBarcode } = handleBarcode()
const { exportToPDF } = pdfPrinter()
const { formatDate } = useDate()

const allSelected = computed(() => {
    if (!props.purchase.length) return false
    return props.purchase.every(item => item.selected === true)
})

const toggleSelectAll = (checked) => {
    props.purchase.forEach(item => {
        item.selected = checked
    })
}

const onView = (item) => {
    emit('onView', item)
}

const buildBarcodeQuery = (purchaseBill, userName) => {
    const qrcodeData = JSON.stringify({
        ref_no: purchaseBill.bill_number,
        ref_date: purchaseBill.date,
        created_by: userName,
        created_date_time: formatDate(new Date())
    })
    return `?code=${purchaseBill.bill_number}&qrcode=${qrcodeData}`
}

const goToPrint = (id, isDynamic) => {
    if (!props.companyInfo) {
        showError('Company info not loaded yet')
        return
    }

    printBill(id, isDynamic).catch(() => {
        showError('Failed to print purchase bill')
    })
}

const printBill = async (id, isDynamic) => {
    const user = store.state.user.user
    const res = await fetchPurchaseBill(id, `?company_id=${route.params.companyId}`)

    if (!res || !res.status) {
        showError('Purchase bill not found')
        return
    }

    const purchaseBill = res.data
    const barcodeRes = await fetchBarcode(buildBarcodeQuery(purchaseBill, user.name))

    exportToPDF(props.companyInfo, purchaseBill, barcodeRes.barcode, barcodeRes.qrcode, user.name, 1, false, props.customizationPdfFields, isDynamic)
}
</script>

<style scoped>
.table-responsive {
    overflow: visible;
}

.dropdown-menu {
    min-width: 180px;
}
</style>
